import { kebabCase } from 'lodash/fp';
import { useMemo, useState, VFC } from 'react';

import Accordion from 'dashboard/components/Accordion';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  caseAttachmentsFor,
  getTokensToDisplayValue,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import { CaseTemplateStatus } from 'dashboard/models/case-template';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowActionMeta, WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionCreateCaseV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

interface ParamsCreateCaseV1Props extends ParamsBaseProps {
  caseTemplates: WorkflowActionMeta['case-templates'];
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionCreateCaseV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (
    newParams: WorkflowActionCreateCaseV1['params'],
    template?: TemplateFilter,
  ) => void;
}

const ParamsCreateCaseV1: VFC<ParamsCreateCaseV1Props> = ({
  step,
  caseTemplates,
  editable,
  errors,
  params,
  trigger,
  usableObjects,
  updateActionParams,
  updateStep,
}) => {
  const hasCaseTemplatePerm = usePermission(Permission.CaseTemplateView);
  function handleEditInputFields(value: Record<string, string>) {
    updateActionParams({
      ...params,
      'case-fields': Object.keys(value).reduce(
        (acc: Record<string, string>, elem: string) => {
          acc[elem] = value[elem];
          return acc;
        },
        {} as Record<string, string>,
      ),
    });
  }

  const selectCaseTemplate = (value: string) => {
    const ct = caseTemplates.find((ct) => ct.token === value);
    if (!ct) return;

    setSelectedCaseTemplate(ct);
    updateActionParams(
      {
        ...params,
        'case-template-token': value,
        'case-fields': {},
      },
      { type: ApiModel.Case, id: value },
    );
  };

  const selectCaseObject = (values: string[]) => {
    updateActionParams({
      ...params,
      'case-objects': values,
    });
  };

  const possibleCaseObjects = useMemo(() => caseAttachmentsFor(usableObjects), [usableObjects]);
  const activeCaseTemplates = caseTemplates.filter((ct) => ct.status === CaseTemplateStatus.Active);

  let possibleCaseTemplates = activeCaseTemplates.map(({ name, prefix, token: value }) => ({
    label: `${name} (${prefix})`,
    value,
  }));

  // Add archived case template option only if current step has this case template selected.
  // This preserves backwards-compatibility by allowing the case template to be visible in the workflow runs page.
  if (
    params['case-template-token'] &&
    !activeCaseTemplates.map((ct) => ct.token).includes(params['case-template-token'])
  ) {
    const currentCaseTemplate = caseTemplates.find(
      (ct) => ct.token === params['case-template-token'],
    );

    if (currentCaseTemplate) {
      possibleCaseTemplates = [
        ...possibleCaseTemplates,
        {
          label: `${currentCaseTemplate.name} (${currentCaseTemplate.prefix})`,
          value: currentCaseTemplate.token,
        },
      ];
    }
  }

  const [selectedCaseTemplate, setSelectedCaseTemplate] = useState(
    caseTemplates.find((ct) => ct.token === params['case-template-token']),
  );
  const isTemplateAvailable = useMemo(() => {
    return selectedCaseTemplate || !params['case-template-token'];
  }, [params, selectedCaseTemplate]);
  const fieldOptions = useMemo(() => {
    const caseTemplate = caseTemplates.find((ct) => ct.token === params['case-template-token']);
    return (caseTemplate?.['field-schema'] || []).reduce(
      (acc, fs) => ({
        ...acc,
        [kebabCase(fs.key)]: fs.label || fs.key,
      }),
      {},
    );
  }, [caseTemplates, params]);

  const [showEditPanel, { toggle: toggleCaseFields, disable: closeCaseFields }] = useToggle(false);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionCreateCaseV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Case Template'
        htmlFor='params-create-case__case-template-input'
        helpText={
          hasCaseTemplatePerm
            ? isTemplateAvailable
              ? undefined
              : 'The current case template is not available to you at this time.'
            : 'Your role does not have permissions to view case templates.'
        }
      />
      <ReactSelect
        value={params['case-template-token']}
        options={possibleCaseTemplates}
        onChange={selectCaseTemplate}
        id='params-create-case__case-template'
        disabled={!editable || !hasCaseTemplatePerm}
        placeholder={
          hasCaseTemplatePerm ? (isTemplateAvailable ? undefined : 'UNAVAILABLE') : 'RESTRICTED'
        }
        hasError={hasErrorFor('case-template-token')}
      />
      {renderErrorMessageFor('case-template-token')}
      <Spacer height={4} />
      {isTemplateAvailable && (
        <Accordion title='Case Fields' defaultExpanded>
          {showEditPanel && Object.keys(fieldOptions).length > 0 && (
            <FieldMappingsPanel
              title='Case fields'
              usableObjects={usableObjects}
              fieldMappings={params['case-fields'] ?? {}}
              presetFields={fieldOptions}
              onOutsideClick={closeCaseFields}
              onUpdateFieldMappings={handleEditInputFields}
            />
          )}
          <Spacer height={2} />
          <FieldsList
            fields={params['case-fields']}
            fieldKey='case-fields'
            editable={
              editable && selectedCaseTemplate != null && Object.keys(fieldOptions).length > 0
            }
            toggleEditPanel={toggleCaseFields}
            tokensToDisplayValue={getTokensToDisplayValue(usableObjects)}
            placeholderEmptyText={
              Object.keys(fieldOptions).length > 0
                ? 'No fields selected'
                : 'Case template has no fields configurations'
            }
            errors={errors}
          />
        </Accordion>
      )}
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <Spacer height={2} />
        <ParamLabel labelText=' Case Objects' htmlFor='params-create-case__case-objects-input' />
        <ReactSelect
          value={params['case-objects']}
          isMulti
          options={possibleCaseObjects}
          onChange={selectCaseObject}
          id='params-create-case__case-objects'
          disabled={!editable}
          hasError={hasErrorFor('case-objects')}
        />
        {renderErrorMessageFor('case-objects')}
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsCreateCaseV1;
